import Box from '@mui/material/Box'
import AlertBadge from 'components/UI/AlertBadge/AlertBadge'
import Tooltip from '@mui/material/Tooltip'
import ResetFiltersIcon from 'components/UI/icons/ResetFiltersIcon'
import COLORS from 'lib/colors'
import UploadIcon from '@mui/icons-material/FileUploadOutlined'
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined'
import DownloadingIcon from '@mui/icons-material/Downloading'
import { ExportStatus } from '../components/ExportStatusDialog'
import {
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  GridColDef,
  GridFilterModel,
  Toolbar,
  ToolbarButton,
} from '@mui/x-data-grid-premium'
import Stack from '@mui/material/Stack'
import { Divider, IconButton } from '@mui/material'
import SyncIcon from '@mui/icons-material/Sync'
import FilterListIcon from '@mui/icons-material/FilterList'
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined'
import LeadTableFilterBar, { getVisibleLeadTableFilters } from './LeadTableFilterBar'
import { LeadRow, LookupMap } from './leadTableTypes'

export type LeadTableToolbarProps = {
  onResetFilters: () => void
  onExport: () => void
  onImport: () => void
  refreshTable: () => void
  exportStatus: ExportStatus | null
  filterModel: GridFilterModel
  columns: GridColDef<LeadRow>[]
  onFilterModelChange: (model: GridFilterModel) => void
  permitUpload: boolean
  loading: boolean
  permitAiAnalysis: boolean
  clientIdToName: LookupMap
  clientCampaignIdToName: LookupMap
  vendorIdToName: LookupMap
  degreeProgramIdToName: LookupMap
  statuses: LookupMap
  dispositions: LookupMap
}

export default function LeadTableToolbar({
  onResetFilters,
  onExport,
  onImport,
  refreshTable,
  exportStatus,
  filterModel,
  columns,
  onFilterModelChange,
  permitUpload,
  loading,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  permitAiAnalysis,
  clientIdToName,
  clientCampaignIdToName,
  vendorIdToName,
  degreeProgramIdToName,
  statuses,
  dispositions,
}: LeadTableToolbarProps) {
  const hasVisibleFilters = getVisibleLeadTableFilters(filterModel).length > 0

  const exportCsvIcon = (
    <Box sx={{ position: 'relative', width: 18, height: 18, display: 'block' }}>
      {!!exportStatus && (exportStatus.status === 'running' || exportStatus.status === 'queued') && (
        <Box sx={{ position: 'absolute', left: -5, top: -5, lineHeight: 0 }}>
          <Tooltip title='Click for Status Update'>
            <Box
              component='span'
              sx={{
                display: 'block',
                lineHeight: 0,
                transform: 'scale(0.75)',
                transformOrigin: 'top left',
              }}
            >
              <DownloadingIcon
                sx={{
                  color: COLORS.coastalWaters,
                  display: 'block',
                }}
              />
            </Box>
          </Tooltip>
        </Box>
      )}

      {!!exportStatus && exportStatus.status === 'discarded' && (
        <AlertBadge
          tipTitle='Error'
          sx={{
            left: -5,
            top: -5,
            transform: 'scale(0.75)',
            transformOrigin: 'top left',
          }}
        />
      )}

      {!!exportStatus && exportStatus.status === 'succeeded' && (
        <AlertBadge
          variant='success'
          tipTitle='Success'
          sx={{
            left: -5,
            top: -5,
            transform: 'scale(0.75)',
            transformOrigin: 'top left',
          }}
        />
      )}

      <DownloadIcon
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          fontSize: 18,
        }}
      />
    </Box>
  )

  return (
    <Toolbar style={{ padding: 0, flex: 'unset', flexShrink: 0 }}>
      <Stack sx={{ width: '100%', flexShrink: 0 }}>
        <Stack
          direction='row'
          alignItems='center'
          gap={0}
          divider={
            <Divider
              orientation='vertical'
              sx={{
                height: 24,
                mx: 0.5,
                borderColor: COLORS.mediumGray,
                borderRightWidth: 1,
              }}
            />
          }
          sx={{
            height: 55,
            minHeight: 55,
            flexShrink: 0,
            justifyContent: 'end',
            pr: 2,
            svg: { color: COLORS.slateGray, fontSize: '18px' },
            '& .MuiIconButton-root + .MuiIconButton-root': {
              ml: 0,
            },
          }}
        >
          <Stack direction='row' fontSize='10px'>
            <Tooltip title='Columns'>
              <ColumnsPanelTrigger
                render={
                  <ToolbarButton aria-label='Columns'>
                    <ViewWeekOutlinedIcon fontSize='inherit' />
                  </ToolbarButton>
                }
              />
            </Tooltip>

            <Tooltip title='Filters'>
              <FilterPanelTrigger
                render={
                  <ToolbarButton aria-label='Filters'>
                    <Box sx={{ position: 'relative', lineHeight: 0 }}>
                      <FilterListIcon />

                      {hasVisibleFilters && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: -3,
                            right: -3,
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: COLORS.frenchBlue,
                          }}
                        />
                      )}
                    </Box>
                  </ToolbarButton>
                }
              />
            </Tooltip>

            <Tooltip title='Reset Filters'>
              <IconButton aria-label='Reset Filters' onClick={onResetFilters}>
                <ResetFiltersIcon />
              </IconButton>
            </Tooltip>

            {/* <PivotPanelTrigger render={<ToolbarButton />} /> */}
            {/* <ChartsPanelTrigger render={<ToolbarButton />} /> */}
            {/* {permitAiAnalysis && <AiAssistantPanelTrigger render={<ToolbarButton />} />} */}
          </Stack>

          <Stack direction='row'>
            <Tooltip title='Export CSV'>
              <IconButton aria-label='Export CSV' onClick={onExport}>
                {exportCsvIcon}
              </IconButton>
            </Tooltip>

            {permitUpload && (
              <Tooltip title='Import CSV'>
                <IconButton aria-label='Import CSV' onClick={onImport}>
                  <UploadIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          <Tooltip title='Refresh'>
            <span>
              <IconButton aria-label='Refresh' onClick={refreshTable} disabled={loading}>
                <SyncIcon style={{ transform: 'rotate(45deg)' }} />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        {hasVisibleFilters && (
          <Box sx={{ borderTop: `1px solid ${COLORS.mediumGray}` }}>
            <LeadTableFilterBar
              filterModel={filterModel}
              columns={columns}
              onFilterModelChange={onFilterModelChange}
              clientIdToName={clientIdToName}
              clientCampaignIdToName={clientCampaignIdToName}
              vendorIdToName={vendorIdToName}
              degreeProgramIdToName={degreeProgramIdToName}
              statuses={statuses}
              dispositions={dispositions}
            />
          </Box>
        )}
      </Stack>
    </Toolbar>
  )
}
